import queryString from 'query-string';
import {useCallback, useEffect, useMemo} from 'react';
import {replace} from 'redux-first-history';
import {ROUTER_REDUCER_STATE_ID} from 'src/js/common/constants';
import {useLocalStorage, useLocalStorageTagged} from 'src/js/hooks';
import {useAppDispatch} from 'src/js/store_hooks';

export function asQueryStrings(query: Record<string, any>): Record<string, string> {
  const entries = Object.entries(query).map(([k, v]) => [k, JSON.stringify(v)]);
  return Object.fromEntries(entries);
}
/** format `{foo: 'value', bar: 12}` to `'key=value&bar=12'` */
export function asQueryString(query: Record<string, any>): string {
  return Object.entries(query)
    .filter(([_k, v]) => v != null)
    .map(([k, v]) => [k, JSON.stringify(v)])
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}
function getQuery(): any {
  const entries = Object.entries(queryString.parse(window.location.search)) as [string, string | null][];
  return Object.fromEntries(entries.map(([k, v]) => [k, JSON.parse(v ?? 'null')]));
}
export function useLocationQuery(
  key: string,
  updateUrl: boolean = false,
  reinitializeOn: (query: any) => any = (_query) => '',
  queryOnly: string[] | undefined = undefined
): [any, (partialDiff: any) => void, () => void] {
  const _query = updateUrl ? getQuery() : {};
  // NOTE: allow navigating to same page with different query
  const cachedQuery = useMemo(() => _query, [JSON.stringify(reinitializeOn(_query)), key]); // eslint-disable-line
  const [state, setState, setStateAndRerender] = useLocalStorage(`${key}.LQ`, {} as any, {queryUpdate: cachedQuery, queryOnly});
  const replaceRoute = useCallback((partialUrlDiff) => {
    return (dispatch, getState) => {
      const pathName = getState()[ROUTER_REDUCER_STATE_ID].location?.pathname;
      const queryString = asQueryString({
        ...getQuery(),
        ...partialUrlDiff,
      });
      return dispatch(
        replace({
          pathname: pathName,
          search: queryString,
        })
      );
    };
  }, []);
  const dispatch = useAppDispatch();
  const setQuery = useCallback(
    (partialDiff: any) => {
      setStateAndRerender({...state, ...partialDiff});
      if (updateUrl) {
        dispatch(replaceRoute(partialDiff));
      }
    },
    [updateUrl, dispatch, replaceRoute, state, setStateAndRerender]
  );
  useEffect(() => {
    if (updateUrl) {
      dispatch(replaceRoute(state));
    }
  }, []); // eslint-disable-line
  const cleanQuery = useCallback(() => {
    setState({});
  }, [setState]);
  return [state, setQuery, cleanQuery];
}

/** deprecated */
export function useLocationNonQuery(key: string) {
  // NOTE: sometimes we need to return a Set<...>
  const [state, setState, setStateAndRerender] = useLocalStorageTagged(`${key}.NQ`, {} as any);
  const cleanNonQuery = useCallback(() => {
    setState({});
  }, [setState]);
  return {nonQuery: state, setNonQuery: setStateAndRerender, cleanNonQuery};
}
